<template>
  <div v-if="introduction == null" class="test-section">
    <div class="test-section-content">
      <div>
        <div class="test-circle test-circle--active"/>
      </div>
      <div class="test-section-content-label">
        <router-link :to="toCommencer">
          Vidéo d'introduction <span class="test-section-content-label-time">(2 min)</span>
        </router-link>
      </div>
    </div>
  </div>
  <div v-else-if="!ifIntroductionFinished" class="test-section">
    <div class="test-section-content test-section-content--active">
      <div>
        <div class="test-circle test-circle--active"/>
      </div>
      <div class="test-section-content-label">
        <router-link :to="toQuestion">
          Vidéo d'introduction <span class="test-section-content-label-time">(2 min)</span>
        </router-link>
        <span class="test-section-content-label-time">
          <span id="videoProgress">{{ videoProgressText }}</span>
        </span>
      </div>
    </div>
  </div>
  <div v-else class="test-section test-section--active">
    <div class="test-section-content">
      <div class="test-icon"><img alt="" src="@/assets/img/check.png"></div>
      <div class="test-section-content-label">Vidéo d'introduction <span class="test-section-content-label-time">(2 min)</span></div>
      <button class="btn-replay-video" @click="showReplayModal = true">▶</button>
    </div>

    <!-- Modal pour revoir la vidéo -->
    <div v-if="showReplayModal" class="replay-modal" @click="closeModalOnBackdrop">
      <div class="replay-modal-content" @click.stop>
        <button class="replay-modal-close" @click="showReplayModal = false">✕</button>
        <div v-if="!replayVideoError" class="video-wrapper">
          <video
              ref="replayVideoPlayer"
              class="replay-video-player"
              controls
              controlsList="nodownload"
              preload="metadata"
              @canplay="onReplayCanPlay"
              @error="onReplayVideoError"
              @loadedmetadata="onReplayVideoLoaded"
              @loadstart="onReplayLoadStart"
          >
            <source v-if="hasWebm()" :src="webmSrc" type="video/webm">
            <source v-if="hasMp4()" :src="mp4Src" type="video/mp4">
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
          <span v-if="!replayVideoLoaded" class="replay-spinner-border"/>
        </div>
        <div v-if="replayVideoError" class="video-error-notification">
          <div class="error-icon">⚠️</div>
          <div class="error-message">
            <strong>Erreur de chargement de la vidéo</strong>
            <p>{{ replayVideoErrorMessage }}</p>
            <button class="retry-button" @click="retryReplayVideo">Réessayer</button>
            <button class="skip-button" @click="showReplayModal = false">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import RouteEnum from "@/router/RouteEnum";

const toQuestion = {name: RouteEnum.TEST_QUESTION, params: {type: 'introduction'}}
const toCommencer = {name: RouteEnum.TEST_COMMENCER, params: {type: 'introduction'}}

const props = defineProps({
  introduction: Object,
  ifIntroductionFinished: Boolean
})

const videoProgressText = ref('')
const showReplayModal = ref(false)
const replayVideoPlayer = ref(null)
const replayVideoLoaded = ref(false)
const replayVideoError = ref(false)
const replayVideoErrorMessage = ref('')
const replayLoadAttempts = ref(0)
const maxReplayLoadAttempts = 3

const videoSrc = 'test_intro.mp4'

const webmSrc = computed(() => {
  const filename = videoSrc.replace(/\.[^/.]+$/, '.webm')
  return require('@/assets/video/' + filename)
})

const mp4Src = computed(() => {
  const filename = videoSrc.replace(/\.[^/.]+$/, '.mp4')
  return require('@/assets/video/' + filename)
})

const hasWebm = () => {
  try {
    const filename = videoSrc.replace(/\.[^/.]+$/, '.webm')
    require('@/assets/video/' + filename)
    return true
  } catch (e) {
    console.error('Failed to load WebM video:', e)
    return false
  }
}

const hasMp4 = () => {
  try {
    const filename = videoSrc.replace(/\.[^/.]+$/, '.mp4')
    require('@/assets/video/' + filename)
    return true
  } catch (e) {
    console.error('Failed to load MP4 video:', e)
    return false
  }
}


const updateProgress = () => {
  if (props.introduction && !props.ifIntroductionFinished) {
    videoProgressText.value = 'En cours...'
  }
}

const closeModalOnBackdrop = (event) => {
  if (event.target.classList.contains('replay-modal')) {
    showReplayModal.value = false
  }
}

const onReplayVideoLoaded = () => {
  replayVideoLoaded.value = true
  replayVideoError.value = false
  replayLoadAttempts.value = 0
  console.log('[Replay Video] Metadata loaded successfully')
}

const onReplayLoadStart = () => {
  console.log('[Replay Video] Loading started', {
    attempt: replayLoadAttempts.value + 1
  })
}

const onReplayCanPlay = () => {
  console.log('[Replay Video] Can play - video is ready')
}

const onReplayVideoError = () => {
  replayLoadAttempts.value++
  const error = replayVideoPlayer.value?.error

  let errorMsg = 'Une erreur est survenue lors du chargement de la vidéo.'
  let detailedError = 'Unknown error'

  if (error) {
    switch (error.code) {
      case 1:
        detailedError = 'MEDIA_ERR_ABORTED'
        errorMsg = 'Le chargement de la vidéo a été interrompu.'
        break
      case 2:
        detailedError = 'MEDIA_ERR_NETWORK'
        errorMsg = 'Une erreur réseau est survenue. Vérifiez votre connexion internet.'
        break
      case 3:
        detailedError = 'MEDIA_ERR_DECODE'
        errorMsg = 'La vidéo ne peut pas être lue (erreur de décodage).'
        break
      case 4:
        detailedError = 'MEDIA_ERR_SRC_NOT_SUPPORTED'
        errorMsg = 'Le format de la vidéo n\'est pas supporté par votre navigateur.'
        break
      default:
        detailedError = `Error code: ${error.code}`
    }
  }

  console.error('[Replay Video] Load error', {
    errorCode: error?.code,
    errorMessage: error?.message,
    detailedError,
    attempt: replayLoadAttempts.value,
    userAgent: navigator.userAgent
  })

  if (replayLoadAttempts.value < maxReplayLoadAttempts) {
    console.log(`[Replay Video] Retrying... (${replayLoadAttempts.value}/${maxReplayLoadAttempts})`)
    setTimeout(() => {
      replayVideoPlayer.value?.load()
    }, 1000)
  } else {
    replayVideoError.value = true
    replayVideoErrorMessage.value = errorMsg
    replayVideoLoaded.value = false
  }
}

const retryReplayVideo = () => {
  console.log('[Replay Video] Manual retry triggered')
  replayVideoError.value = false
  replayVideoErrorMessage.value = ''
  replayLoadAttempts.value = 0
  replayVideoLoaded.value = false
  replayVideoPlayer.value?.load()
}

watch(showReplayModal, async (newValue) => {
  if (newValue) {
    // Reset state when opening modal
    replayVideoLoaded.value = false
    replayVideoError.value = false
    replayVideoErrorMessage.value = ''
    replayLoadAttempts.value = 0

    await nextTick()
    if (replayVideoPlayer.value) {
      replayVideoPlayer.value.play().catch(err => {
        console.error('[Replay Video] Play failed', err)
      })
    }
  }
})

onMounted(() => {
  updateProgress()
})
</script>

<style scoped>
.btn-replay-video {
  background: transparent;
  color: #c9da2a;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 2px 2px;
  margin-left: 1rem;
  line-height: 1;
}

.btn-replay-video:hover {
  transform: scale(1.05);
}


.replay-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.replay-modal-content {
  position: relative;
  max-width: 900px;
  max-height: 90vh;
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.replay-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.replay-modal-close:hover {
  background: rgba(0, 0, 0, 0.9);
}

.video-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.replay-video-player {
  width: 100%;
  height: auto;
  max-height: 90vh;
  display: block;
  background: #000;
  object-fit: contain;
}

.replay-spinner-border {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #0E6076;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.video-error-notification {
  max-width: 440px;
  width: 90%;
  padding: 2rem 1.75rem;
  background: white;
  color: #1f2937;
  border-radius: 16px;
  text-align: center;
  animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.error-message strong {
  display: block;
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #111827;
}

.error-message p {
  margin: 0 0 1.75rem;
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

.retry-button, .skip-button {
  padding: 0.75rem 1.5rem;
  margin: 0.35rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.retry-button {
  background: #0E6076;
  color: white;
}

.retry-button:hover {
  background: #0a4d5f;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 96, 118, 0.3);
}

.skip-button {
  background: #f3f4f6;
  color: #4b5563;
}

.skip-button:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .btn-replay-video {
    margin-left: 0.5rem;
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }

  .replay-modal {
    padding: 0.5rem;
  }

  .replay-modal-close {
    width: 35px;
    height: 35px;
    font-size: 1.3rem;
  }

  .video-error-notification {
    padding: 1.75rem 1.5rem;
    width: 92%;
    max-width: 380px;
  }

  .error-icon {
    font-size: 2.5rem;
  }

  .error-message strong {
    font-size: 1.05rem;
  }

  .error-message p {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .retry-button, .skip-button {
    padding: 0.7rem 1.25rem;
    font-size: 0.9rem;
    min-width: 110px;
  }
}
</style>
